import { LaunchProofPhase1SectionV1Schema, LaunchProofSearchReadinessExcerptV1Schema, } from "../contracts/launch-proof-report.js";
import { PHASE1_CHECK_IDS } from "../contracts/phase1-check-pack.js";
import { WHAT_WE_DO_NOT_PROMISE } from "./compose-search-readiness.js";
import { LAUNCH_PROOF_DISCLAIMERS } from "./launch-proof-disclaimers.js";
import { clientSafeAreaLabelFromCheckId } from "./report-governance-disposition.js";
const NOT_EVALUATED_SUMMARY = "This check has not been evaluated yet. Results appear when server-side checks are wired.";
function phase1SectionFromResult(result) {
    return LaunchProofPhase1SectionV1Schema.parse({
        schemaVersion: "launch-proof-phase1-section.v1.0",
        checkId: result.checkId,
        areaLabel: clientSafeAreaLabelFromCheckId(result.checkId),
        status: result.status,
        clientSafeSummary: result.clientSafeSummary,
        internalSummary: result.internalSummary,
    });
}
function notEvaluatedSection(checkId) {
    return LaunchProofPhase1SectionV1Schema.parse({
        schemaVersion: "launch-proof-phase1-section.v1.0",
        checkId,
        areaLabel: clientSafeAreaLabelFromCheckId(checkId),
        status: "not_evaluated",
        clientSafeSummary: NOT_EVALUATED_SUMMARY,
    });
}
export function buildPhase1Sections(phase1Results) {
    const byId = new Map();
    for (const result of phase1Results ?? []) {
        byId.set(result.checkId, result);
    }
    return PHASE1_CHECK_IDS.map((checkId) => {
        const result = byId.get(checkId);
        if (!result) {
            return notEvaluatedSection(checkId);
        }
        return phase1SectionFromResult(result);
    });
}
export function resolveLaunchProofAggregateStatus(input, phase1Sections) {
    const evaluated = phase1Sections.filter((s) => s.status !== "not_evaluated");
    if (evaluated.length === 0) {
        if (input.qualityGateStatus === "QA_FAIL") {
            return "blocked";
        }
        if (input.qualityGateStatus === "QA_WARN") {
            return "ready_with_warnings";
        }
        return "not_evaluated";
    }
    if (evaluated.some((s) => s.status === "fail")) {
        return "blocked";
    }
    if (evaluated.some((s) => s.status === "warn")) {
        return "ready_with_warnings";
    }
    if (input.qualityGateStatus === "QA_FAIL") {
        return "blocked";
    }
    if (evaluated.every((s) => s.status === "pass") &&
        evaluated.length === PHASE1_CHECK_IDS.length) {
        return "ready";
    }
    if (evaluated.every((s) => s.status === "pass")) {
        return "ready_with_warnings";
    }
    return "not_evaluated";
}
function buildHeadline(aggregateStatus, phase1Sections) {
    const evaluatedCount = phase1Sections.filter((s) => s.status !== "not_evaluated").length;
    if (evaluatedCount === 0) {
        return "Launch proof summary — surface quality checks not yet evaluated.";
    }
    switch (aggregateStatus) {
        case "blocked":
            return "Launch proof summary — blocking issues found in surface quality checks.";
        case "ready_with_warnings":
            return "Launch proof summary — completed with warnings.";
        case "ready":
            return "Launch proof summary — evaluated checks passed at this reference.";
        default:
            return "Launch proof summary — evaluation incomplete.";
    }
}
function buildSearchReadinessExcerpt(input) {
    if (!input.searchReadinessHeadline ||
        !input.searchReadinessQaStatus ||
        !input.searchReadinessCoverage) {
        return undefined;
    }
    return LaunchProofSearchReadinessExcerptV1Schema.parse({
        schemaVersion: "launch-proof-search-readiness-excerpt.v1.0",
        headline: input.searchReadinessHeadline,
        qaStatus: input.searchReadinessQaStatus,
        coverage: input.searchReadinessCoverage,
        staleBanner: input.searchReadinessStaleBanner ?? null,
    });
}
export function composeLaunchProofSections(input) {
    const phase1Sections = buildPhase1Sections(input.phase1Results);
    const aggregateStatus = resolveLaunchProofAggregateStatus(input, phase1Sections);
    const headline = buildHeadline(aggregateStatus, phase1Sections);
    return {
        aggregateStatus,
        headline,
        phase1Sections,
        searchReadinessExcerpt: buildSearchReadinessExcerpt(input),
        whatWeDoNotPromise: [...WHAT_WE_DO_NOT_PROMISE],
        launchProofDisclaimers: [...LAUNCH_PROOF_DISCLAIMERS],
    };
}
//# sourceMappingURL=compose-launch-proof.js.map